"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.defaultProps = exports.default = exports.BannerCompact = void 0;
require("./BannerCompact.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _renderObject = require("../../util/renderObject/renderObject");
var _Icon = _interopRequireDefault(require("../Icon/Icon"));
var _Link = _interopRequireDefault(require("../Link/Link"));
var _LoaderGhostCard = _interopRequireDefault(require("../LoaderGhostCard/LoaderGhostCard"));
var _Text = _interopRequireDefault(require("../Text/Text"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/**
 * BannerCompact component -
 * A compact banner for displaying a headline and message with an optional icon and actions.
 *
 * @method
 * @param { BannerCompactProps } props
 * @returns {JSX.Element} The rendered BannerCompact component.
 */

const BannerCompact = props => {
  const {
    backgroundColor,
    body,
    borderColor = defaultProps.borderColor,
    cornerRadius = defaultProps.cornerRadius,
    elevation = defaultProps.elevation,
    externalActionElement,
    headline,
    headlineTag = defaultProps.headlineTag,
    iconName,
    isLoading = defaultProps.isLoading,
    loaderHeightLg,
    loaderHeightMd,
    loaderHeightSm,
    loaderHeightXl,
    secondaryLink,
    secondaryTooltip,
    textColor = defaultProps.textColor,
    textPosition = defaultProps.textPosition,
    tooltip
  } = props;
  if (isLoading) {
    return /*#__PURE__*/_react.default.createElement("div", {
      className: "pal-c-BannerCompact"
    }, /*#__PURE__*/_react.default.createElement(_LoaderGhostCard.default, {
      className: "pal-c-BannerCompact__Loader",
      style: {
        '--pal-loaderghost-card-height-sm': loaderHeightSm,
        '--pal-loaderghost-card-height-md': loaderHeightMd,
        '--pal-loaderghost-card-height-lg': loaderHeightLg,
        '--pal-loaderghost-card-height-xl': loaderHeightXl
      }
    }));
  }
  if (!headline) {
    return;
  }

  // Show inverted true for the icon when textColor is knockout
  const isIconInverted = textColor === 'content-knockout';
  const componentClassName = (0, _classnames.default)('pal-c-BannerCompact__bannerContainer', {
    'pal-c-BannerCompact--backgroundColor': backgroundColor,
    'pal-c-BannerCompact--borderColor': borderColor,
    'pal-c-BannerCompact__corners--rounded': cornerRadius,
    [`pal-c-BannerCompact__elevation--${elevation}`]: elevation
  });
  const componentStyle = {
    ...(backgroundColor && {
      '--backgroundColor': `var( --pal-colors-default-${backgroundColor} )`
    }),
    ...(borderColor && {
      '--borderColor': `var( --pal-colors-default-${borderColor} )`
    }),
    ...(cornerRadius && {
      '--cornerRadius': `var( --pal-${cornerRadius} )`
    })
  };
  return /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-BannerCompact"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: componentClassName,
    style: componentStyle
  }, iconName && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-BannerCompact__iconContainer"
  }, /*#__PURE__*/_react.default.createElement(_Icon.default, {
    name: iconName,
    size: "xl",
    inverted: isIconInverted
  })), /*#__PURE__*/_react.default.createElement("div", {
    className: (0, _classnames.default)({
      [`pal-c-BannerCompact__textPosition--${textPosition}`]: textPosition
    })
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-BannerCompact__textContainer"
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-BannerCompact__headlineContainer"
  }, headline && /*#__PURE__*/_react.default.createElement(_Text.default, {
    htmlTag: headlineTag,
    textStyle: "title-sm",
    className: "pal-c-BannerCompact__title",
    textColor: textColor
  }, headline), tooltip && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-BannerCompact__tooltipContainer"
  }, (0, _renderObject.renderObject)(tooltip))), body && /*#__PURE__*/_react.default.createElement(_Text.default, {
    htmlTag: "p",
    className: "pal-c-BannerCompact__body",
    textColor: textColor
  }, body)), externalActionElement && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-BannerCompact__actionContainer"
  }, (0, _renderObject.renderObject)(externalActionElement)))), secondaryLink && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-BannerCompact__secondaryActionContainer"
  }, /*#__PURE__*/_react.default.createElement(_Link.default, _extends({}, secondaryLink, {
    variant: "tertiary",
    textStyle: "displayBody-sm"
  })), secondaryTooltip && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-BannerCompact__secondaryTooltipContainer"
  }, (0, _renderObject.renderObject)(secondaryTooltip))));
};

/**
 * Property type definitions
 * @typedef BannerCompactProps
 * @type {object}
 * @property {string} backgroundColor - The background color of the banner.
 * @property {string} body - The body text of the banner.
 * @property {string} borderColor - The border color of the banner.
 * @property {string} cornerRadius - Sets the border radius of the banner.
 * @property {(undefined|'low'|'medium'|'high')} elevation - The elevation (box-shadow) of the banner.
 * @property {object} externalActionElement - A React element to be rendered as the primary action.
 * @property {string} headline - The main headline text of the banner.
 * @property {string} headlineTag - The HTML tag for the headline.
 * @property {string} iconName - The name of the icon to display.
 * @property {boolean} isLoading - To show the loading state of the component.
 * @property {string} loaderHeightLg - The height of the loader on LG inflections (ex - "11rem")
 * @property {string} loaderHeightMd - The height of the loader on MD inflections (ex - "11rem")
 * @property {string} loaderHeightSm - The height of the loader on SM inflections (ex - "11rem")
 * @property {string} loaderHeightXl - The height of the loader on XL inflections (ex - "11rem")
 * @property {object} secondaryLink - An object for rendering a secondary link below the banner.
 * @property {object} secondaryTooltip - Tooltip for the secondary action.
 * @property {('content-default'|'content-knockout')} textColor - The color of the text content.
 * @property {('inline'|'stacked')} textPosition - The layout of the text and action elements.
 * @property {object} tooltip - Tooltip for the primary action.
 */
exports.BannerCompact = BannerCompact;
const propTypes = exports.propTypes = {
  /** The background color of the banner. */
  backgroundColor: _propTypes.default.string,
  /** The body text of the banner. */
  body: _propTypes.default.string,
  /** The border color of the banner. */
  borderColor: _propTypes.default.string,
  /** Sets the border radius of the banner. */
  cornerRadius: _propTypes.default.string,
  /** The elevation (box-shadow) of the banner. */
  elevation: _propTypes.default.oneOf([undefined, 'low', 'medium', 'high']),
  /** A React element to be rendered as the primary action. */
  externalActionElement: _propTypes.default.object,
  /** The main headline text of the banner. */
  headline: _propTypes.default.string,
  /** The HTML tag for the headline. */
  headlineTag: _propTypes.default.string,
  /** The name of the icon to display. */
  iconName: _propTypes.default.string,
  /** To show the loading state of the component. */
  isLoading: _propTypes.default.bool,
  /** The height of the loader on LG inflections (ex - "11rem") */
  loaderHeightLg: _propTypes.default.string,
  /** The height of the loader on MD inflections (ex - "11rem") */
  loaderHeightMd: _propTypes.default.string,
  /** The height of the loader on SM inflections (ex - "11rem") */
  loaderHeightSm: _propTypes.default.string,
  /** The height of the loader on XL inflections (ex - "11rem") */
  loaderHeightXl: _propTypes.default.string,
  /** An object for rendering a secondary link below the banner. */
  secondaryLink: _propTypes.default.object,
  /** Tooltip for the secondary action. */
  secondaryTooltip: _propTypes.default.object,
  /** The color of the text content. */
  textColor: _propTypes.default.oneOf(['content-default', 'content-knockout']),
  /** The layout of the text and action elements. */
  textPosition: _propTypes.default.oneOf(['inline', 'stacked']),
  /** Tooltip for the primary action. */
  tooltip: _propTypes.default.object
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} borderColor=undefined - The default border color.
 * @property {string} cornerRadius=undefined - The default border radius.
 * @property {string} elevation=undefined - No elevation by default.
 * @property {string} headlineTag='h2' - The default HTML tag for the headline.
 * @property {string} textColor='content-default' - The default text color.
 * @property {string} textPosition='inline' - The default layout for text and actions.
 * @property {boolean} isLoading=false - Set default isLoading to false
 */
const defaultProps = exports.defaultProps = {
  borderColor: undefined,
  cornerRadius: undefined,
  elevation: undefined,
  headlineTag: 'h2',
  textColor: 'content-default',
  textPosition: 'inline',
  isLoading: false
};
BannerCompact.propTypes = propTypes;
var _default = exports.default = BannerCompact;